/**
 * Strip dynamic-zone entries flagged `hidden: true` from a Strapi entry
 * before it enters Angular's HTTP transfer cache / component tree.
 *
 * Spec 33: the dynamic-zone-renderer already skips hidden entries at
 * render time (src/app/core/dynamic-zone/dynamic-zone-renderer.ts),
 * but `provideClientHydration(withHttpTransferCacheOptions)` serialises
 * the raw HTTP response into `<script id="ng-state">…</script>`, so
 * hrefs inside hidden sections remain discoverable by crawlers. Filtering
 * here — at the data boundary — purges them before the transfer cache
 * captures the payload.
 *
 * Applies to three arrays that carry section components:
 *   - `sections`      — static-page, landing-page, partner
 *   - `extraSections` — case-study, asset-type-page, archive-page, industry-page
 *   - `body`          — event-page (section components)
 *
 * Note: `insight-page.body` and `news-page.body` carry `blocks.*` components
 * which do not have a `hidden` field. This helper is safe on those because
 * elements without `hidden` are kept as-is (the filter predicate rejects
 * only `hidden === true`, not `hidden === undefined` or `hidden === false`).
 *
 * Pure + null-safe: returns a shallow copy only when the input is an object.
 * Null / undefined / primitive inputs are returned as-is. Arrays that are
 * absent from the entry or that aren't arrays are left untouched.
 */

type MaybeHidden = { hidden?: boolean };

export function stripHiddenSections<T>(entry: T): T {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }

  const e = entry as T & {
    sections?: MaybeHidden[];
    extraSections?: MaybeHidden[];
    body?: MaybeHidden[];
  };

  const drop = <U extends MaybeHidden>(
    arr: U[] | undefined,
  ): U[] | undefined =>
    Array.isArray(arr) ? arr.filter((s) => s?.hidden !== true) : arr;

  const next = { ...e };
  if ('sections' in e) next.sections = drop(e.sections);
  if ('extraSections' in e) next.extraSections = drop(e.extraSections);
  if ('body' in e) next.body = drop(e.body);

  return next as T;
}
